import React, { useState, useEffect, useRef } from 'react';
import { API_ENDPOINTS, apiRequest } from '../../../api/config';
import { useTheme } from '../ThemeContext';
import { getTranslation } from '../translations';  // 导入翻译函数

// 可复制链接组件 - 参考 CloakList
const CopyableLink = ({ url, label }) => {
  const { darkMode, language } = useTheme();
  const t = getTranslation(language);
  const [showToast, setShowToast] = useState(false);
  const textRef = useRef(null);
  
  const fallbackCopyToClipboard = (text) => {
    const textArea = document.createElement('textarea');
    textArea.value = text;
    textArea.style.position = 'fixed';
    textArea.style.left = '-999999px';
    textArea.style.top = '-999999px';
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();
    
    let success = false;
    try {
      success = document.execCommand('copy');
    } catch (err) {
      success = false;
    }
    
    document.body.removeChild(textArea);
    return success;
  };
  
  const copyToClipboard = async (text) => {
    if (navigator.clipboard && window.isSecureContext) {
      try {
        await navigator.clipboard.writeText(text);
        return true;
      } catch (err) {
        return fallbackCopyToClipboard(text);
      }
    } else {
      return fallbackCopyToClipboard(text);
    }
  };
  
  const handleCopy = async (e) => {
    e.stopPropagation();
    const success = await copyToClipboard(url);
    if (success) {
      setShowToast(true);
      setTimeout(() => setShowToast(false), 1500);
    }
  };
  
  if (!url) return <span style={{color: 'var(--t3)'}}>-</span>;
  
  const displayLabel = label || url;
  
  return (
    <div style={{position: 'relative', display: 'inline-block'}}>
      <button
        onClick={handleCopy}
        ref={textRef}
        className="td-link"
        style={{
          background: 'none',
          border: 'none',
          padding: '2px 4px',
          cursor: 'pointer',
          color: darkMode ? 'var(--accent)' : 'var(--accent-d)',
          fontFamily: 'inherit',
          fontSize: 'inherit',
          textDecoration: 'none',
          transition: 'all 0.2s'
        }}
        title={t('copyLink')}
      >
        {displayLabel}
        {showToast && (
          <span style={{
            position: 'absolute',
            bottom: '100%',
            left: '50%',
            transform: 'translateX(-50%)',
            background: 'var(--bg-card)',
            color: 'var(--green)',
            padding: '4px 8px',
            borderRadius: '4px',
            fontSize: '12px',
            whiteSpace: 'nowrap',
            boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
            marginBottom: '4px'
          }}>
            ✓ {t('copied')}
          </span>
        )}
      </button>
    </div>
  );
};

const LogStatusBadge = ({ status }) => {
  const { darkMode } = useTheme();
  let badgeClass = "log-status-badge ";
  
  if (status === 'Success' || status === '成功') {
    badgeClass += darkMode ? "log-status-success-dark" : "log-status-success";
  } else if (status === 'Warning' || status === '警告') {
    badgeClass += darkMode ? "log-status-warning-dark" : "log-status-warning";
  } else if (status === 'Error' || status === '错误') {
    badgeClass += darkMode ? "log-status-error-dark" : "log-status-error";
  } else {
    badgeClass += darkMode ? "log-status-default-dark" : "log-status-default";
  }
  
  return <span className={badgeClass}>{status}</span>;
};

const LogTypeIcon = ({ type }) => {
  const getIconClass = () => {
    if (type === 'Access' || type === '访问') return 'log-type-icon log-type-access';
    if (type === 'Create' || type === '创建') return 'log-type-icon log-type-create';
    if (type === 'Modify' || type === '修改') return 'log-type-icon log-type-modify';
    if (type === 'Delete' || type === '删除') return 'log-type-icon log-type-delete';
    if (type === 'Error' || type === '错误') return 'log-type-icon log-type-error';
    return 'log-type-icon log-type-default';
  };
  
  if (type === 'Access' || type === '访问') {
    return (
      <svg xmlns="http://www.w3.org/2000/svg" className={getIconClass()} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path>
        <polyline points="15 3 21 3 21 9"></polyline>
        <line x1="10" y1="14" x2="21" y2="3"></line>
      </svg>
    );
  } else if (type === 'Create' || type === '创建') {
    return (
      <svg xmlns="http://www.w3.org/2000/svg" className={getIconClass()} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10"></circle>
        <line x1="12" y1="8" x2="12" y2="16"></line>
        <line x1="8" y1="12" x2="16" y2="12"></line>
      </svg>
    );
  } else if (type === 'Modify' || type === '修改') {
    return (
      <svg xmlns="http://www.w3.org/2000/svg" className={getIconClass()} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
        <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
      </svg>
    );
  } else if (type === 'Delete' || type === '删除') {
    return (
      <svg xmlns="http://www.w3.org/2000/svg" className={getIconClass()} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="3 6 5 6 21 6"></polyline>
        <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
        <line x1="10" y1="11" x2="10" y2="17"></line>
        <line x1="14" y1="11" x2="14" y2="17"></line>
      </svg>
    );
  } else if (type === 'Error' || type === '错误') {
    return (
      <svg xmlns="http://www.w3.org/2000/svg" className={getIconClass()} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10"></circle>
        <line x1="12" y1="8" x2="12" y2="12"></line>
        <line x1="12" y1="16" x2="12.01" y2="16"></line>
      </svg>
    );
  }
  
  return (
    <svg xmlns="http://www.w3.org/2000/svg" className={getIconClass()} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10"></circle>
      <line x1="12" y1="16" x2="12" y2="12"></line>
      <line x1="12" y1="8" x2="12.01" y2="8"></line>
    </svg>
  );
};

const CloakLogs = () => {
  const { darkMode, language } = useTheme();  // 获取语言设置
  const t = getTranslation(language);  // 创建翻译函数
  
  const [loading, setLoading] = useState(true);
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(20);
  const [searchTerm, setSearchTerm] = useState('');
  // 筛选输入态（不触发请求）
  const [redirectFilterInput, setRedirectFilterInput] = useState('');
  const [cloakIdInput, setCloakIdInput] = useState('');
  // 已提交的筛选（触发请求）
  const [appliedRedirectFilter, setAppliedRedirectFilter] = useState('');
  const [appliedCloakId, setAppliedCloakId] = useState('');
  const [error, setError] = useState(null);
  const [selectedCloak, setSelectedCloak] = useState(null);
  const [detailsLoading, setDetailsLoading] = useState(false);
  
  // 斗篷列表相关状态
  const [cloaks, setCloaks] = useState([]);
  const [cloaksLoading, setCloaksLoading] = useState(false);
  const [cloakSearchOpen, setCloakSearchOpen] = useState(false);
  const [cloakSearchQuery, setCloakSearchQuery] = useState('');
  const cloakDropdownRef = useRef(null);
  const [searchingCloaks, setSearchingCloaks] = useState(false);
  
  // 防止 StrictMode 双重执行
  const lastDepsRef = useRef(null);
  
  // 加载斗篷列表
  useEffect(() => {
    const loadCloaks = async () => {
      try {
        setCloaksLoading(true);
        const params = new URLSearchParams({
          page: 1,
          perPage: 100
        });
        
        const response = await apiRequest(`${API_ENDPOINTS.CLOAK.LIST}?${params.toString()}`, {}, true, 'GET');
        const data = response?.data || response;
        
        if (data.items && Array.isArray(data.items)) {
          setCloaks(data.items);
        }
      } catch (error) {
        console.error('[CloakLogs] Failed to load cloaks:', error);
      } finally {
        setCloaksLoading(false);
      }
    };
    
    loadCloaks();
  }, []);
  
  // 搜索斗篷时调用接口
  useEffect(() => {
    if (!cloakSearchQuery || cloakSearchQuery.length < 1) {
      return;
    }
    
    const searchTimeout = setTimeout(async () => {
      try {
        setSearchingCloaks(true);
        const params = new URLSearchParams({
          page: 1,
          perPage: 50,
          search: cloakSearchQuery
        });
        
        const response = await apiRequest(`${API_ENDPOINTS.CLOAK.LIST}?${params.toString()}`, {}, true, 'GET');
        const data = response?.data || response;
        
        if (data.items && Array.isArray(data.items)) {
          setCloaks(data.items);
        }
      } catch (error) {
        console.error('[CloakLogs] Failed to search cloaks:', error);
      } finally {
        setSearchingCloaks(false);
      }
    }, 300); // 300ms 延迟，避免频繁请求
    
    return () => clearTimeout(searchTimeout);
  }, [cloakSearchQuery]);
  
  // 点击外部关闭下拉框
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (cloakDropdownRef.current && !cloakDropdownRef.current.contains(event.target)) {
        setCloakSearchOpen(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  
  // 过滤斗篷列表
  const filteredCloaks = cloaks.filter(cloak => 
    cloak.name.toLowerCase().includes(cloakSearchQuery.toLowerCase()) ||
    cloak.id.toLowerCase().includes(cloakSearchQuery.toLowerCase())
  );
  
  // 加载数据
  useEffect(() => {
    // 防止 StrictMode 开发环境下重复调用
    const depsKey = JSON.stringify({ page, perPage, appliedRedirectFilter, appliedCloakId, language });
    if (lastDepsRef.current === depsKey) return;
    lastDepsRef.current = depsKey;

    const loadData = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // 构建查询参数
        const params = {
          page,
          perPage: perPage
        };
        
        // 添加可选的筛选条件
        if (appliedCloakId) {
          params.cloakId = appliedCloakId;
        }
        
        if (appliedRedirectFilter) {
          params.redirectTo = appliedRedirectFilter;
        }
        
        console.log('[CloakLogs] Sending request params:', params, 'Timestamp:', new Date().toISOString());
        
        // 发送API请求，使用apiRequest
        const response = await apiRequest(API_ENDPOINTS.CLOAK.ACCESS_LOGS, params);
        
        console.log('[CloakLogs] Retrieved logs data:', response);
        
        // Java后端返回 {status, data} 格式
        const logsData = response?.data?.items || response?.items || [];
        const logsTotal = response?.data?.total || response?.total || 0;
        const logsPage = response?.data?.page || response?.page || 1;
        const logsPerPage = response?.data?.perPage || response?.perPage || 20;
        
        console.log('[CloakLogs] Log entry example:', logsData.length > 0 ? logsData[0] : 'No data');
        
        // apiRequest会自动解析JSON和处理错误
        setLogs(logsData);
        setTotal(logsTotal);
        setPage(logsPage);
        setPerPage(logsPerPage);
        
        setLoading(false);
      } catch (error) {
        console.error('[CloakLogs] Failed to load logs:', error);
        let errorMessage = t('failedToLoadLogs');
        
        if (error.isAuthError) {
          errorMessage = language === 'zh' ? '您的登录已过期或未授权访问此资源，请重新登录' : 'Your login has expired or you are not authorized to access this resource, please login again';
        } else if (error.message) {
          errorMessage = error.message;
        }
        
        setError(errorMessage);
        setLoading(false);
      }
    };
    
    loadData();
  }, [page, perPage, appliedRedirectFilter, appliedCloakId, language]);
  
  // 格式化日期
  const formatDate = (dateValue) => {
    if (!dateValue) return language === 'zh' ? '未知时间' : 'Unknown time';
    
    let date;
    
    // 如果已经是 Date 对象
    if (dateValue instanceof Date) {
      date = dateValue;
    } else {
      const dateStr = String(dateValue);
      
      // ISO 格式: "2026-04-18T20:29:05" -> 解析为本地时间
      if (dateStr.includes('T')) {
        const match = dateStr.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})/);
        if (match) {
          date = new Date(
            parseInt(match[1]),
            parseInt(match[2]) - 1,
            parseInt(match[3]),
            parseInt(match[4]),
            parseInt(match[5]),
            parseInt(match[6])
          );
        }
      }
      
      // 如果上面解析失败，尝试直接解析
      if (!date || isNaN(date.getTime())) {
        date = new Date(dateStr);
      }
    }
    
    if (isNaN(date.getTime())) {
      return String(dateValue); // 返回原始值作为后备
    }
    
    return date.toLocaleString(language === 'zh' ? 'zh-CN' : 'en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false
    });
  };
  
  // mode字段映射为文本描述
  const getModeText = (mode) => {
    if (language === 'zh') {
      const modeMap = {
        'cloak': '斗篷',
        'fullOpen': '全开',
        'audit': '审核'
      };
      return modeMap[mode] || '未知';
    } else {
      const modeMap = {
        'cloak': 'Cloak',
        'fullOpen': 'Full Open',
        'audit': 'Audit'
      };
      return modeMap[mode] || 'Unknown';
    }
  };
  
  // 模式标签组件
  const ModeBadge = ({ mode }) => {
    const { darkMode } = useTheme();
    let badgeClass = "mode-badge ";
    
    switch (mode) {
      case 'cloak':
        badgeClass += darkMode ? "mode-badge-cloak-dark" : "mode-badge-cloak";
        break;
      case 'fullOpen':
        badgeClass += darkMode ? "mode-badge-fullopen-dark" : "mode-badge-fullopen";
        break;
      case 'audit':
        badgeClass += darkMode ? "mode-badge-audit-dark" : "mode-badge-audit";
        break;
      default:
        badgeClass += darkMode ? "mode-badge-default-dark" : "mode-badge-default";
    }
    
    return <span className={badgeClass}>{getModeText(mode)}</span>;
  };
  
  // 搜索过滤
  const filteredLogs = logs.filter(log => {
    if (!searchTerm) return true;
    
    return (
      (log.cloakName && log.cloakName.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (log.visitorIp && log.visitorIp.includes(searchTerm)) ||
      (log.countryCode && log.countryCode.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (log.device && log.device.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (log.browser && log.browser.toLowerCase().includes(searchTerm.toLowerCase()))
    );
  });
  
  // 处理页码变化
  const handlePageChange = (newPage) => {
    if (newPage > 0 && newPage <= Math.ceil(total / perPage)) {
      setPage(newPage);
    }
  };
  
  // 返回分页按钮
  const renderPagination = () => {
    const totalPages = Math.ceil(total / perPage);
    if (totalPages <= 1) return null;
    
    const pageButtons = [];
    let startPage = Math.max(1, page - 2);
    let endPage = Math.min(totalPages, page + 2);
    
    // 确保显示至少5个页码按钮（如果有的话）
    if (endPage - startPage < 4 && totalPages > 4) {
      if (startPage === 1) {
        endPage = Math.min(5, totalPages);
      } else if (endPage === totalPages) {
        startPage = Math.max(1, totalPages - 4);
      }
    }
    
    // 添加第一页按钮
    if (startPage > 1) {
      pageButtons.push(
        <button 
          key="first" 
          className="page-btn"
          onClick={() => handlePageChange(1)}
        >
          1
        </button>
      );
      
      // 添加省略号
      if (startPage > 2) {
        pageButtons.push(<span key="ellipsis1" className="page-ellipsis">...</span>);
      }
    }
    
    // 添加页码按钮
    for (let i = startPage; i <= endPage; i++) {
      pageButtons.push(
        <button
          key={i}
          className={`page-btn ${i === page ? 'page-btn-active' : ''}`}
          onClick={() => handlePageChange(i)}
        >
          {i}
        </button>
      );
    }
    
    // 添加最后一页按钮
    if (endPage < totalPages) {
      // 添加省略号
      if (endPage < totalPages - 1) {
        pageButtons.push(<span key="ellipsis2" className="page-ellipsis">...</span>);
      }
      
      pageButtons.push(
        <button 
          key="last" 
          className="page-btn"
          onClick={() => handlePageChange(totalPages)}
        >
          {totalPages}
        </button>
      );
    }
    
    return (
      <div className="pagination-wrap">
        <nav className="pagination-nav">
          <button 
            className="page-btn"
            disabled={page === 1}
            onClick={() => handlePageChange(page - 1)}
          >
            {t('previous')}
          </button>
          
          {pageButtons}
          
          <button 
            className="page-btn"
            disabled={page === totalPages || totalPages === 0}
            onClick={() => handlePageChange(page + 1)}
          >
            {t('next')}
          </button>
        </nav>
      </div>
    );
  };
  
  // 重置筛选器
  const resetFilters = () => {
    setSearchTerm('');
    setRedirectFilterInput('');
    setCloakIdInput('');
    setCloakSearchQuery('');
    setCloakSearchOpen(false);
    setAppliedRedirectFilter('');
    setAppliedCloakId('');
    setPage(1);
  };
  
  // 获取斗篷详情
  const fetchCloakDetails = async (id) => {
    if (!id) return;
    
    try {
      setDetailsLoading(true);
      console.log('Getting cloak details ID:', id);
      
      // 使用 API_ENDPOINTS.CLOAK.DETAIL 函数
      const response = await apiRequest(API_ENDPOINTS.CLOAK.DETAIL(id));
      console.log('Retrieved cloak details response:', response);
      
      // API返回格式是 {status, message, data}，需要从 data 中提取详情
      const details = response?.data || response;
      
      if (!details || response?.status === 'error') {
        throw new Error(details?.message || details?.error || 'Failed to get cloak details');
      }
      
      console.log('Parsed cloak details:', details);
      setSelectedCloak(details);
    } catch (error) {
      console.error('Failed to get cloak details:', error);
      // 设置错误状态，稍后在模态框中显示
      setSelectedCloak({ error: error.message || t('failedToGetDetails') });
    } finally {
      setDetailsLoading(false);
    }
  };
  
  // 处理点击斗篷名称
  const handleCloakNameClick = (id, cloakName) => {
    if (id) {
      console.log('Clicked on cloak name, getting details, ID:', id, 'Name:', cloakName);
      console.log('API URL:', API_ENDPOINTS.CLOAK.DETAIL(id));
      
      // 如果有现成的斗篷名称，先设置一个基本详情对象，避免加载时显示空白
      if (cloakName) {
        setSelectedCloak({
          id: id,
          name: cloakName,
          isBasicInfo: true  // 标记这是基本信息，不是完整详情
        });
      }
      
      fetchCloakDetails(id);
    }
  };
  
  // 关闭详情模态框
  const closeDetailsModal = () => {
    setSelectedCloak(null);
  };
  
  // 渲染斗篷详情模态框 - 参考 EditCloakModal 布局风格
  const renderDetailsModal = () => {
    if (!selectedCloak) return null;

    return (
      <div className="modal-overlay open" onClick={(e) => e.target === e.currentTarget && closeDetailsModal()} style={{opacity: 1, pointerEvents: 'auto'}}>
        <div className="modal" style={{width: '744px', maxHeight: '92vh'}}>
          <div className="modal-hd">
            <span className="modal-title">
              {selectedCloak.error ? t('failedToGetDetails') : (selectedCloak.name || t('cloakDetails'))}
            </span>
            <span className="modal-close" onClick={closeDetailsModal}>×</span>
          </div>

          <div className="modal-body" style={{padding: '20px', overflowY: 'auto', maxHeight: 'calc(92vh - 160px)'}}>
            {detailsLoading ? (
              <div className="modal-loading">
                <div className="spinner"></div>
                <div className="modal-loading-text">{t('loadingDetails')}</div>
              </div>
            ) : selectedCloak.error ? (
              <div className="modal-error">
                <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" fill="none" viewBox="0 0 24 24" stroke="var(--red)" style={{margin: '0 auto 12px'}}>
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
                </svg>
                <p style={{color: 'var(--red)'}}>{selectedCloak.error}</p>
              </div>
            ) : selectedCloak.isBasicInfo ? (
              // 基本信息模式 - 表单风格
              <div className="form-group">
                <label className="form-label">{t('cloakName')}</label>
                <div style={{padding: '10px 12px', background: darkMode ? 'var(--bg-input)' : 'var(--bg-card)', borderRadius: '6px', border: '1px solid var(--border)', color: 'var(--t1)'}}>
                  {selectedCloak.name || '-'}
                </div>
              </div>
            ) : (
              // 完整详情 - 参考 EditCloakModal 布局风格
              <>
                {/* 斗篷名称和模式 */}
                <div style={{display: 'flex', gap: '16px', marginBottom: '20px'}}>
                  <div className="form-group" style={{flex: 1, marginBottom: 0}}>
                    <label className="form-label">{t('cloakName')}</label>
                    <div style={{padding: '10px 12px', background: darkMode ? 'var(--bg-input)' : 'var(--bg-card)', borderRadius: '6px', border: '1px solid var(--border)', color: 'var(--t1)'}}>
                      {selectedCloak.name || '-'}
                    </div>
                  </div>
                  <div className="form-group" style={{width: '180px', marginBottom: 0}}>
                    <label className="form-label">{t('mode')}</label>
                    <div style={{padding: '10px 12px', background: darkMode ? 'var(--bg-input)' : 'var(--bg-card)', borderRadius: '6px', border: '1px solid var(--border)', display: 'flex', alignItems: 'center'}}>
                      <ModeBadge mode={selectedCloak.mode} />
                    </div>
                  </div>
                </div>

                {/* 短链接 */}
                <div className="form-group">
                  <label className="form-label">{t('shortLink')}</label>
                  <div style={{display: 'flex', gap: '8px', alignItems: 'center'}}>
                    <div style={{flex: 1, padding: '10px 12px', background: darkMode ? 'var(--bg-input)' : 'var(--bg-card)', borderRadius: '6px', border: '1px solid var(--border)', fontFamily: 'monospace', wordBreak: 'break-all', color: 'var(--t1)'}}>
                      {selectedCloak.shortLink || '-'}
                    </div>
                    {selectedCloak.shortLink && (
                      <CopyableLink url={selectedCloak.shortLink} label="📋" />
                    )}
                  </div>
                </div>

                {/* 真实链接 */}
                <div className="form-group">
                  <label className="form-label">{t('realLink')}</label>
                  <div style={{padding: '10px 12px', background: darkMode ? 'var(--bg-input)' : 'var(--bg-card)', borderRadius: '6px', border: '1px solid var(--border)', wordBreak: 'break-all'}}>
                    {selectedCloak.realUrl ? (
                      <CopyableLink url={selectedCloak.realUrl} />
                    ) : '-'}
                  </div>
                </div>

                {/* 审核链接 */}
                {selectedCloak.auditUrl && (
                  <div className="form-group">
                    <label className="form-label">{t('auditLink')}</label>
                    <div style={{padding: '10px 12px', background: darkMode ? 'var(--bg-input)' : 'var(--bg-card)', borderRadius: '6px', border: '1px solid var(--border)', wordBreak: 'break-all'}}>
                      <CopyableLink url={selectedCloak.auditUrl} />
                    </div>
                  </div>
                )}

                {/* 目标地区 */}
                {selectedCloak.selectedRegions && selectedCloak.selectedRegions.length > 0 && (
                  <div className="form-group">
                    <label className="form-label">{t('targetRegions')}</label>
                    <div style={{padding: '10px 12px', background: darkMode ? 'var(--bg-input)' : 'var(--bg-card)', borderRadius: '6px', border: '1px solid var(--border)', display: 'flex', flexWrap: 'wrap', gap: '6px'}}>
                      {selectedCloak.selectedRegions.map((region, index) => {
                        // 后端返回的region结构：{ code: string, name: string }
                        const codeValue = region.code || '';
                        const nameValue = region.name || codeValue || '';
                        
                        return (
                          <span key={index} className="region-badge">
                            {nameValue} ({codeValue})
                          </span>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* 统计数据 */}
                <div className="modal-stats-grid" style={{marginTop: '20px'}}>
                  <div className="text-center">
                    <div className="modal-stats-label">{t('totalVisits')}</div>
                    <div className="modal-stats-value">{selectedCloak.visits || 0}</div>
                  </div>
                  <div className="text-center">
                    <div className="modal-stats-label">{t('realLinkVisits')}</div>
                    <div className="modal-stats-value modal-stats-value-success">{selectedCloak.realVisits || 0}</div>
                  </div>
                  <div className="text-center">
                    <div className="modal-stats-label">{t('auditLinkVisits')}</div>
                    <div className="modal-stats-value modal-stats-value-warning">{selectedCloak.auditVisits || 0}</div>
                  </div>
                </div>

                {/* 创建时间 */}
                <div style={{marginTop: '20px', paddingTop: '16px', borderTop: '1px solid var(--border)', textAlign: 'center', color: 'var(--t2)', fontSize: '12px'}}>
                  {t('created')}: {formatDate(selectedCloak.created || selectedCloak.createdAt)}
                </div>
              </>
            )}
          </div>

          <div className="modal-footer">
            <button
              type="button"
              className="btn btn-default"
              onClick={closeDetailsModal}
            >
              {t('close')}
            </button>
          </div>
        </div>
      </div>
    );
  };
  
  if (loading) {
    return (
      <div className="dashboard-loading">
        <div className="spinner"></div>
      </div>
    );
  }
  
  return (
    <div className="page active">
      <div className="page-hd">
        <div>
          <div className="page-title">{t('cloakLogs')}</div>
        </div>
      </div>

      <div className="filter-bar">
        <div className="filter-item">
          <span className="filter-label">{t('selectCloak')}</span>
          <div className="cloak-search-dropdown" ref={cloakDropdownRef} style={{position: 'relative', flex: 1}}>
            <div 
              className="cloak-search-input-wrapper"
              onClick={() => setCloakSearchOpen(!cloakSearchOpen)}
              style={{
                display: 'flex',
                alignItems: 'center',
                background: darkMode ? 'var(--bg-input)' : 'var(--bg-card)',
                border: '1px solid var(--border)',
                borderRadius: '6px',
                padding: '8px 12px',
                cursor: 'pointer',
                minHeight: '38px'
              }}
            >
              <input
                type="text"
                className="fi"
                placeholder={t('searchOrSelectCloak')}
                value={cloakIdInput ? (cloaks.find(c => c.id === cloakIdInput)?.name || '') : cloakSearchQuery}
                onChange={(e) => {
                  setCloakSearchQuery(e.target.value);
                  setCloakSearchOpen(true);
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  setCloakSearchOpen(true);
                }}
                onFocus={() => setCloakSearchOpen(true)}
                style={{
                  background: 'none',
                  border: 'none',
                  outline: 'none',
                  flex: 1,
                  color: 'var(--t1)',
                  fontSize: '14px'
                }}
              />
              <span style={{color: 'var(--t3)', fontSize: '12px', marginLeft: '8px'}}>
                {cloakSearchOpen ? '▲' : '▼'}
              </span>
            </div>
            
            {cloakSearchOpen && (
              <div 
                className="cloak-dropdown-list"
                style={{
                  position: 'absolute',
                  top: '100%',
                  left: 0,
                  right: 0,
                  marginTop: '4px',
                  background: darkMode ? 'var(--bg-card)' : 'white',
                  border: '1px solid var(--border)',
                  borderRadius: '6px',
                  maxHeight: '200px',
                  overflowY: 'auto',
                  overflowX: 'hidden',
                  zIndex: 1000,
                  boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
                }}
              >
                <div
                  onClick={() => {
                    setCloakIdInput('');
                    setCloakSearchQuery('');
                    setCloakSearchOpen(false);
                  }}
                  style={{
                    padding: '8px 12px',
                    cursor: 'pointer',
                    color: cloakIdInput === '' ? 'var(--accent)' : 'var(--t1)',
                    background: cloakIdInput === '' ? (darkMode ? 'var(--bg-hover)' : '#f0f0f0') : 'transparent',
                    borderBottom: '1px solid var(--border)'
                  }}
                >
                  {t('allCloaks')}
                </div>
                {searchingCloaks ? (
                  <div style={{
                    padding: '12px',
                    textAlign: 'center',
                    color: 'var(--t3)',
                    fontSize: '12px'
                  }}>
                    {t('searching')}
                  </div>
                ) : filteredCloaks.map(cloak => (
                  <div
                    key={cloak.id}
                    onClick={() => {
                      setCloakIdInput(cloak.id);
                      setCloakSearchQuery('');
                      setCloakSearchOpen(false);
                    }}
                    style={{
                      padding: '8px 12px',
                      cursor: 'pointer',
                      color: cloakIdInput === cloak.id ? 'var(--accent)' : 'var(--t1)',
                      background: cloakIdInput === cloak.id ? (darkMode ? 'var(--bg-hover)' : '#f0f0f0') : 'transparent',
                      borderBottom: '1px solid var(--border)'
                    }}
                  >
                    {cloak.name}
                  </div>
                ))}
                {!searchingCloaks && filteredCloaks.length === 0 && (
                  <div style={{
                    padding: '12px',
                    textAlign: 'center',
                    color: 'var(--t3)',
                    fontSize: '12px'
                  }}>
                    {t('noCloaksFound')}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
        <div className="filter-item">
          <span className="filter-label">{t('redirectTo')}</span>
          <select className="fi" value={redirectFilterInput} onChange={(e) => setRedirectFilterInput(e.target.value)}>
            <option value="">{t('all')}</option>
            <option value="target">{t('allow')}</option>
            <option value="audit">{t('block')}</option>
          </select>
        </div>
        <div className="filter-btns">
          <button className="btn btn-primary" onClick={() => {
            setAppliedCloakId(cloakIdInput);
            setAppliedRedirectFilter(redirectFilterInput);
            setPage(1);
          }}>🔍 {t('search')}</button>
          <button className="btn btn-default" onClick={resetFilters}>↺ {t('reset')}</button>
        </div>
      </div>

      <div className="table-wrap">
        {filteredLogs.length > 0 ? (
          <table>
            <thead>
              <tr>
                <th>{t('time')}</th>
                <th>{t('ipInfo')}</th>
                <th>{t('redirectTo')}</th>
                <th>{t('mode')}</th>
                <th>{t('cloakId')}</th>
                <th>{t('deviceBrowser')}</th>
              </tr>
            </thead>
            <tbody>
              {filteredLogs.map(log => (
                <tr key={log.id}>
                  <td className="td-mono">{formatDate(log.accessTime)}</td>
                  <td className="td-mono">{log.visitorIp}</td>
                  <td>
                    <span className={log.redirectTo === 'target' ? 'badge badge-green' : 'badge badge-red'}>
                      {log.redirectTo === 'target' ? t('allow') : t('block')}
                    </span>
                  </td>
                  <td><ModeBadge mode={log.mode} /></td>
                  <td>
                    <div style={{display: 'flex', alignItems: 'center', gap: '8px'}}>
                      <CopyableLink 
                        url={log.shortLink || log.cloakName || ''} 
                        label={log.cloakName || t('unknownCloak')} 
                      />
                      <button
                        onClick={() => handleCloakNameClick(log.cloakId, log.cloakName || t('unknownCloak'))}
                        className="btn btn-default btn-xs"
                        title={t('cloakDetails')}
                      >
                        📋
                      </button>
                    </div>
                  </td>
                  <td>{log.device || t('unknownDevice')}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div className="empty">{t('noLogs')}</div>
        )}
        {total > 0 && (
          <div className="pagination">
            <span>{t('totalRecords', { 0: total })}</span>
            <div className="pagi-btns">
              <button className="pagi-btn" onClick={() => handlePageChange(page - 1)} disabled={page === 1}>‹</button>
              {Array.from({ length: Math.min(5, Math.ceil(total / perPage)) }, (_, i) => {
                let pageNum;
                const totalPages = Math.ceil(total / perPage);
                if (totalPages <= 5) {
                  pageNum = i + 1;
                } else if (page <= 3) {
                  pageNum = i + 1;
                } else if (page >= totalPages - 2) {
                  pageNum = totalPages - 4 + i;
                } else {
                  pageNum = page - 2 + i;
                }
                return (
                  <button
                    key={pageNum}
                    onClick={() => handlePageChange(pageNum)}
                    className={`pagi-btn ${page === pageNum ? 'active' : ''}`}
                  >
                    {pageNum}
                  </button>
                );
              })}
              <button className="pagi-btn" onClick={() => handlePageChange(page + 1)} disabled={page >= Math.ceil(total / perPage)}>›</button>
            </div>
          </div>
        )}
      </div>

      {renderDetailsModal()}
    </div>
  );
};

export default CloakLogs; 